import { Button, Checkbox, RiFilter3Line, Stack } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ThreatType } from '@/api/rest/resources/types/fact';
import { Picker, PickerContent, PickerTrigger } from '@/components/widgets/o/Form/Picker';

type HabitatIntactnessThreatsPickerProps = {
  threats: ThreatType[];
  selectedThreats: ThreatType[];
  setSelectedThreats: (threats: ThreatType[]) => void;
};

export const HabitatIntactnessThreatsPicker = ({
  threats,
  selectedThreats,
  setSelectedThreats,
}: HabitatIntactnessThreatsPickerProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const [pickerSelectedThreats, setPickerSelectedThreats] = useState<ThreatType[]>(selectedThreats);

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const resetPicker = () => {
    setSelectedThreats(selectedThreats);
    setPickerSelectedThreats(selectedThreats);
  };

  const handleCancelClick = () => {
    resetPicker();
    closeDropdown();
  };

  const handleSubmitClick = () => {
    setSelectedThreats(pickerSelectedThreats);
    setPickerSelectedThreats(pickerSelectedThreats);
    closeDropdown();
  };

  if (threats.length === 0) {
    return null;
  }

  return (
    <Picker
      open={isOpen}
      onOpenChange={(open) => {
        resetPicker();
        setIsOpen(open);
      }}
    >
      <PickerTrigger leftAdornment={<RiFilter3Line />} data-testid='threat-picker-trigger'>
        {pickerSelectedThreats?.length
          ? t('shared.projects.project.conservation.interactiveMap.labels.filtersApplied', {
              count: pickerSelectedThreats.length,
            })
          : t('shared.projects.project.conservation.interactiveMap.labels.filterByThreats')}
      </PickerTrigger>

      <PickerContent data-testid='threat-picker-content'>
        <Stack spacing={4}>
          <Stack spacing={2}>
            {threats.map((threat) => {
              return (
                <Checkbox
                  key={threat}
                  id={threat}
                  checked={pickerSelectedThreats?.includes(threat)}
                  onCheckedChange={() => {
                    const set = new Set(pickerSelectedThreats);
                    if (set.has(threat)) {
                      set.delete(threat);
                    } else {
                      set.add(threat);
                    }
                    setPickerSelectedThreats(Array.from(set));
                  }}
                >
                  <label htmlFor={threat}>{t(`global.threatTypes.${threat}`)}</label>
                </Checkbox>
              );
            })}
          </Stack>
          <Stack direction='row-reverse' spacing={2}>
            <Button variant='contained' size='small' onClick={handleSubmitClick}>
              {t('shared.projects.project.conservation.interactiveMap.labels.applyFilter')}
            </Button>
            <Button variant='text' size='small' onClick={handleCancelClick}>
              {t('global.ui.buttons.cancel')}
            </Button>
          </Stack>
        </Stack>
      </PickerContent>
    </Picker>
  );
};
